/**
 * Cue times as timecode (1.9.0): shown and typed in the file's own timecode (the media's start timecode for an asset,
 * 00:00:00:00 for a sequence), with the keypad entry rules of the editor's TimecodeField ("1000" is 00:00:10:00).
 * At 29.97 and 59.94 a typed ":" before the frames is read as drop frame, as the player shows it (";").
 */
import { framesAfterStart, framesToTicks, framesToTimecode, formatTimecode, isDropFrameRate, ticksPerFrame, ticksToFrames, type Rational } from '@prism/contracts';
import { parseTimecodeEntry } from '../pages/create/timelineTools';

/** The rate the asset's times are in, as the Player has it. */
export const assetRate = (timing: { sourceFrameRate?: Rational | null } | undefined, frameRate?: Rational | null): Rational =>
  timing?.sourceFrameRate ?? frameRate ?? { num: 25, den: 1 };

/** The timecode of media time `ticks` (the frame it is in) with the start timecode `startFrames`. */
export function cueTimecode(ticks: number, rate: Rational, startFrames: number): string {
  return formatTimecode(framesToTimecode(startFrames + ticksToFrames(Math.max(0, ticks), rate), rate));
}

/** A typed timecode as frames of the timecode count (absolute), or null. */
function entryFrames(text: string, rate: Rational): number | null {
  let t = text.trim();
  // Drop-frame rates: the frames separator is read as drop frame whichever is typed.
  if (isDropFrameRate(rate)) t = t.replace(/^(\d{1,2}:\d{2}:\d{2})[:.]/, '$1;');
  const ticks = parseTimecodeEntry(t, rate);
  if (ticks == null) return null;
  return Math.round(ticks / ticksPerFrame(rate));
}

/**
 * A typed cue time in ticks of media time, on a frame: the typed timecode minus the start timecode. Null when it is
 * not a timecode or lies before the start timecode. 1.9.1: with the media's length, one earlier than the start
 * timecode that falls within the media on the next day is read so (a recording from 23:30 to 00:40: 00:10:00:00).
 */
export function parseCueTimecode(text: string, rate: Rational, startFrames: number, durationTicks?: number | null): number | null {
  const f = entryFrames(text, rate);
  if (f == null) return null;
  if (f >= startFrames) return framesToTicks(f - startFrames, rate);
  if (!durationTicks) return null;
  const next = framesToTicks(framesAfterStart(f, startFrames, rate) - startFrames, rate);
  return next <= durationTicks ? next : null;
}

/** A signed length typed as a timecode ("-00:00:01:12", "+112", "2:00"), in ticks on whole frames, or null. */
export function parseSignedTimecode(text: string, rate: Rational): number | null {
  const m = /^\s*([+\-−])?\s*(.+)$/.exec(text);
  if (!m) return null;
  const sign = m[1] === '-' || m[1] === '−' ? -1 : 1;
  const f = entryFrames(m[2]!, rate);
  if (f == null) return null;
  return sign * framesToTicks(f, rate);
}

export { signedTimecode } from '../pages/create/timelineTools';
